import React, { useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, GraduationCap, User, ChalkboardTeacher, UsersThree, CheckCircle } from "@phosphor-icons/react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { cn } from "@/lib/utils";

const ROLE_OPTIONS = [
  { key: "student", icon: User, tone: "text-violet-600 bg-violet-500/10" },
  { key: "parent", icon: UsersThree, tone: "text-sky-600 bg-sky-500/10" },
  { key: "teacher", icon: ChalkboardTeacher, tone: "text-emerald-600 bg-emerald-500/10" },
];

export default function Register() {
  const { user, register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "student", password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error(t("auth.register.errors.nameRequired"));
    if (!f.email.trim()) return toast.error(t("auth.register.errors.emailRequired"));
    if (f.password.length < 6) return toast.error(t("auth.register.errors.passwordShort"));
    if (f.password !== f.confirm) return toast.error(t("auth.register.errors.passwordMismatch"));
    setBusy(true);
    const res = await register({
      name: f.name.trim(),
      email: f.email.trim().toLowerCase(),
      phone: f.phone.trim() || null,
      role: f.role,
      password: f.password,
    });
    setBusy(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(t("auth.register.success"));
    // Role-based redirect
    if (res.user.role === "teacher") navigate("/teacher-portal", { replace: true });
    else navigate("/portal", { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex bg-background" data-testid="register-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-red/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary-foreground/10 flex items-center justify-center">
              <GraduationCap size={22} weight="duotone" />
            </div>
            <div>
              <div className="font-bold text-xl" style={{ fontFamily: "Manrope" }}>Bilinguist</div>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">School OS</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="inline-block px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.2em] bg-brand-red">
            {t("auth.register.badge")}
          </div>
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.05]" style={{ fontFamily: "Manrope" }}>
            {t("auth.register.brandTitle")}
          </h1>
          <p className="text-primary-foreground/70 text-base max-w-md leading-relaxed">
            {t("auth.register.brandSubtitle")}
          </p>
          <ul className="space-y-3 pt-4 border-t border-primary-foreground/10">
            {["personalPortal", "trackProgress", "multilingual"].map((k) => (
              <li key={k} className="flex items-center gap-3 text-sm text-primary-foreground/80">
                <CheckCircle size={18} weight="duotone" className="text-brand-red" />
                {t(`auth.register.perks.${k}`)}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-xs opacity-60">© 2026 Bilinguist. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative overflow-y-auto">
        <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
        <div className="w-full max-w-md space-y-6 py-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">{t("auth.register.header")}</div>
            <h2 className="text-3xl font-bold" style={{ fontFamily: "Manrope" }}>{t("auth.register.title")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("auth.register.subtitle")}</p>
          </div>

          {/* Role picker */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">{t("auth.register.rolePickLabel")}</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {ROLE_OPTIONS.map(({ key, icon: Icon, tone }) => {
                const active = f.role === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`role-pick-${key}`}
                    onClick={() => setF({ ...f, role: key })}
                    className={cn(
                      "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all text-left",
                      active ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 hover:border-border",
                    )}
                  >
                    <div className={cn("w-10 h-10 rounded-md flex items-center justify-center", tone)}>
                      <Icon size={20} weight="duotone" />
                    </div>
                    <div className="text-sm font-semibold w-full text-center">{t(`roles.${key}`)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider">{t("auth.register.fields.name")}</Label>
              <Input id="name" data-testid="register-name" required
                value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder={t("auth.register.fields.namePh")} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">{t("auth.email")}</Label>
              <Input id="email" data-testid="register-email" type="email" required
                value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                placeholder={t("auth.emailPlaceholder")} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider">
                {t("auth.register.fields.phone")} <span className="text-muted-foreground normal-case font-normal">({t("common.optional")})</span>
              </Label>
              <Input id="phone" data-testid="register-phone"
                value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                placeholder="+382 …" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wider">{t("auth.password")}</Label>
              <div className="relative mt-1.5">
                <Input id="pw" data-testid="register-password" type={show ? "text" : "password"} required
                  value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
                  placeholder="••••••••" className="h-11 pr-10" />
                <button type="button" onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t("auth.register.passwordHint")}</p>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider">{t("auth.register.fields.confirmPassword")}</Label>
              <Input id="confirm" data-testid="register-confirm" type={show ? "text" : "password"} required
                value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })}
                placeholder="••••••••" className="mt-1.5 h-11" />
            </div>

            <Button data-testid="register-submit" type="submit" disabled={busy}
              className="w-full h-11 rounded-full font-semibold">
              {busy ? t("auth.register.creating") : t("auth.register.createBtn")}
            </Button>

            <div className="text-center text-xs pt-1">
              <span className="text-muted-foreground">{t("auth.register.alreadyHaveAccount")} </span>
              <Link to="/login" data-testid="back-to-login" className="font-semibold text-primary hover:underline">
                {t("auth.signIn")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
